"use client";

/**
 * P4-01 · Client cart store.
 *
 * The real cart store the storefront reads from. It hydrates from
 * `cart.get` (server-authoritative lines + summary) and drives the mutations;
 * each mutation returns a fresh server-authoritative `CartView`. Qty/remove use
 * a short-lived optimistic projection for immediate feedback, then replace it
 * with that server response; any refusal rolls back and re-fetches server truth.
 *
 * Keeps the `useCart()` surface the P3-08 buy-box seam relies on (`count`,
 * `addItem`) and extends it with the lines/summary and qty/remove mutations the
 * mini-cart (P4-02) and full cart (P4-03) render. Named `use-cart-stub` /
 * `CartStubProvider` until P14-03: the "stub" was the P3-08 seam's name, the
 * swap to the real store happened in P4, and the name outlived it by ten
 * phases — this is the real client cart store.
 */
import * as React from "react";

import { useSignedIn } from "~/components/auth/signed-in-provider";
import type { AddItemInput } from "~/lib/cart/cart-interface";
import type { CartLineView, CartSummary, CartView } from "~/lib/cart/summary";
import { api } from "~/trpc/react";

interface CartContextValue {
  /** The full cart, or null before the first load. */
  cart: CartView | null;
  lines: CartLineView[];
  summary: CartSummary | null;
  /** Total units in the cart (the header badge count). */
  count: number;
  /** True while the initial cart load is in flight. */
  isLoading: boolean;
  /** Whether the mini-cart drawer is open (P4-02). */
  isOpen: boolean;
  /** Open the mini-cart drawer. */
  open: () => void;
  /** Close the mini-cart drawer. */
  close: () => void;
  /** Add a line; resolves to the updated cart, rejects on server refusal. */
  addItem: (input: AddItemInput) => Promise<CartView>;
  /** Set a line's quantity (0 removes it). */
  updateQuantity: (itemId: string, quantity: number) => Promise<CartView>;
  /** Remove a line. */
  removeItem: (itemId: string) => Promise<CartView>;
}

const CartContext = React.createContext<CartContextValue | null>(null);

/** Immediate projection only; every successful mutation replaces it with server truth. */
export function optimisticQuantityView(
  view: CartView,
  itemId: string,
  quantity: number,
): CartView {
  const line = view.lines.find((candidate) => candidate.id === itemId);
  if (!line) return view;

  const nextQuantity = Math.max(0, quantity);
  const nextLineTotal = line.unitPriceMinor * nextQuantity;
  const lineDelta = nextLineTotal - line.lineTotalMinor;
  const itemDelta = nextQuantity - line.quantity;
  const subtotalMinor = Math.max(0, view.summary.subtotalMinor + lineDelta);
  const threshold = view.summary.freeShippingThresholdMinor;

  return {
    ...view,
    lines:
      nextQuantity === 0
        ? view.lines.filter((candidate) => candidate.id !== itemId)
        : view.lines.map((candidate) =>
            candidate.id === itemId
              ? { ...candidate, quantity: nextQuantity, lineTotalMinor: nextLineTotal }
              : candidate,
          ),
    summary: {
      ...view.summary,
      itemCount: Math.max(0, view.summary.itemCount + itemDelta),
      subtotalMinor,
      grandTotalMinor: Math.max(0, view.summary.grandTotalMinor + lineDelta),
      freeShippingRemainingMinor:
        threshold == null ? null : Math.max(0, threshold - subtotalMinor) || null,
    },
  };
}

export function CartProvider({
  children,
  hasCartCookie = false,
}: {
  children: React.ReactNode;
  /**
   * P17A-10 · SSR-resolved (`~/server/cart/cart-service#hasCartCookie`): true
   * when the visitor already carries a verified `vx_cart` cookie. A fresh
   * visitor with neither a cookie nor a session has no cart to fetch, so
   * `cart.get` stays off until there's a reason to believe one exists —
   * either this, or being signed in (whose cart the server owns regardless
   * of any cookie), or adding an item locally (below). Defaults to `false`
   * for callers (tests, `kitchen-sink`) that don't thread the SSR value.
   */
  hasCartCookie?: boolean;
}) {
  const signedIn = useSignedIn();
  // Sticky once true: flipped locally right after a successful add so the
  // query enables itself without waiting on the next navigation's SSR read to
  // notice the cookie the mutation just set.
  const [cartActive, setCartActive] = React.useState(hasCartCookie);
  const enabled = signedIn || cartActive;

  const utils = api.useUtils();
  const cartQuery = api.cart.get.useQuery(undefined, {
    enabled,
    // The cart is small and per-user; mutations refresh it directly, so we don't
    // need aggressive background refetching.
    staleTime: 60_000,
  });

  const applyView = React.useCallback(
    (view: CartView) => utils.cart.get.setData(undefined, view),
    [utils],
  );

  const addMutation = api.cart.addItem.useMutation({
    onSuccess: (view) => {
      setCartActive(true);
      applyView(view);
    },
  });
  const updateMutation = api.cart.updateQuantity.useMutation({
    onMutate: async ({ itemId, quantity }) => {
      await utils.cart.get.cancel();
      const previous = utils.cart.get.getData();
      if (previous) {
        utils.cart.get.setData(undefined, optimisticQuantityView(previous, itemId, quantity));
      }
      return { previous };
    },
    onSuccess: applyView,
    // A guarded update can lose to another tab. Re-read server truth before the
    // shopper tries again (also safe for any other update failure).
    onError: (_error, _input, context) => {
      if (context?.previous) utils.cart.get.setData(undefined, context.previous);
      void utils.cart.get.invalidate();
    },
  });
  const removeMutation = api.cart.removeItem.useMutation({
    onMutate: async ({ itemId }) => {
      await utils.cart.get.cancel();
      const previous = utils.cart.get.getData();
      if (previous) {
        utils.cart.get.setData(undefined, optimisticQuantityView(previous, itemId, 0));
      }
      return { previous };
    },
    onSuccess: applyView,
    onError: (_error, _input, context) => {
      if (context?.previous) utils.cart.get.setData(undefined, context.previous);
      void utils.cart.get.invalidate();
    },
  });

  const addItem = React.useCallback(
    (input: AddItemInput) => addMutation.mutateAsync(input),
    [addMutation],
  );
  const updateQuantity = React.useCallback(
    (itemId: string, quantity: number) => {
      const expectedQuantity = cartQuery.data?.lines.find(
        (line) => line.id === itemId,
      )?.quantity;
      if (expectedQuantity == null) {
        return Promise.reject(new Error("That cart line is no longer available."));
      }
      return updateMutation.mutateAsync({ itemId, quantity, expectedQuantity });
    },
    [cartQuery.data?.lines, updateMutation],
  );
  const removeItem = React.useCallback(
    (itemId: string) => removeMutation.mutateAsync({ itemId }),
    [removeMutation],
  );

  const [isOpen, setIsOpen] = React.useState(false);
  const open = React.useCallback(() => setIsOpen(true), []);
  const close = React.useCallback(() => setIsOpen(false), []);

  const cart = cartQuery.data ?? null;
  const value = React.useMemo<CartContextValue>(
    () => ({
      cart,
      lines: cart?.lines ?? [],
      summary: cart?.summary ?? null,
      count: cart?.summary.itemCount ?? 0,
      isLoading: cartQuery.isLoading,
      isOpen,
      open,
      close,
      addItem,
      updateQuantity,
      removeItem,
    }),
    [cart, cartQuery.isLoading, isOpen, open, close, addItem, updateQuantity, removeItem],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

/** The cart context, or null when rendered outside the provider. */
export function useCartContext(): CartContextValue | null {
  return React.useContext(CartContext);
}

export function useCart(): CartContextValue {
  const ctx = useCartContext();
  if (!ctx) {
    throw new Error("useCart must be used within <CartProvider>");
  }
  return ctx;
}
